/**
 * KilometerRegistratie — privéauto zakelijk gebruik, km-vergoeding
 */

import { useState, useEffect, useCallback } from 'react';
import { kilometerregistratieService, Rit, PriveAuto, JaarOverzicht, RelatieOptie } from '../../services/kilometerregistratie.service';
import Toast from '../../components/Toast';
import './KilometerRegistratie.css';

const KilometerRegistratie = () => {
  const [activeTab, setActiveTab] = useState<'ritten' | 'overzicht'>('ritten');
  const [ritten, setRitten] = useState<Rit[]>([]);
  const [autos, setAutos] = useState<PriveAuto[]>([]);
  const [overzicht, setOverzicht] = useState<JaarOverzicht | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [relaties, setRelaties] = useState<RelatieOptie[]>([]);

  // Filters
  const [filterAutoId, setFilterAutoId] = useState<number | null>(null);
  const [filterJaar, setFilterJaar] = useState<number>(new Date().getFullYear());

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editRit, setEditRit] = useState<Rit | null>(null);
  const [formData, setFormData] = useState({
    auto_id: '',
    datum: '',
    bestemming: '',
    omschrijving: '',
    km_heen: '',
    km_terug: '',
    tarief: '0.25',
  });

  // Auto beheer
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [nieuweAutoNaam, setNieuweAutoNaam] = useState('');
  const [nieuweAutoKenteken, setNieuweAutoKenteken] = useState('');
  const [editAutoId, setEditAutoId] = useState<number | null>(null);
  const [editAutoNaam, setEditAutoNaam] = useState('');
  const [editAutoKenteken, setEditAutoKenteken] = useState('');

  // Load autos + relaties
  const loadAutos = useCallback(async () => {
    try {
      const data = await kilometerregistratieService.getAutos();
      setAutos(data);
    } catch (err: any) {
      console.error('Fout bij laden autos:', err);
    }
  }, []);

  useEffect(() => {
    loadAutos();
  }, [loadAutos]);

  useEffect(() => {
    const loadRelaties = async () => {
      try {
        const data = await kilometerregistratieService.getRelaties();
        setRelaties(data);
      } catch (err: any) {
        console.error('Fout bij laden relaties:', err);
      }
    };
    loadRelaties();
  }, []);

  // Load ritten
  const loadRitten = useCallback(async () => {
    try {
      setLoading(true);
      const data = await kilometerregistratieService.getRitten({
        auto_id: filterAutoId || undefined,
        jaar: filterJaar || undefined,
        limit: 500,
      });
      setRitten(data.items);
      setTotal(data.total);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon ritten niet laden');
    } finally {
      setLoading(false);
    }
  }, [filterAutoId, filterJaar]);

  useEffect(() => {
    loadRitten();
  }, [loadRitten]);

  // Load overzicht
  const loadOverzicht = useCallback(async () => {
    if (activeTab !== 'overzicht') return;
    try {
      setLoading(true);
      const data = await kilometerregistratieService.getOverzicht(filterJaar);
      setOverzicht(data);
    } catch (err: any) {
      setError(err.message || 'Kon overzicht niet laden');
    } finally {
      setLoading(false);
    }
  }, [filterJaar, activeTab]);

  useEffect(() => {
    loadOverzicht();
  }, [loadOverzicht]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);

  // Open modal
  const openNewModal = () => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setFormData({
      auto_id: autos.length > 0 ? autos[0].id.toString() : '',
      datum: dateStr,
      bestemming: '',
      omschrijving: '',
      km_heen: '',
      km_terug: '',
      tarief: '0.25',
    });
    setEditRit(null);
    setShowModal(true);
  };

  const openEditModal = (rit: Rit) => {
    setFormData({
      auto_id: rit.auto_id.toString(),
      datum: rit.datum,
      bestemming: rit.bestemming,
      omschrijving: rit.omschrijving || '',
      km_heen: rit.km_heen.toString(),
      km_terug: rit.km_terug.toString(),
      tarief: rit.tarief.toString(),
    });
    setEditRit(rit);
    setShowModal(true);
  };

  // Submit rit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.auto_id || !formData.bestemming || !formData.km_heen) {
      setError('Vul alle verplichte velden in');
      return;
    }

    const kmHeen = parseInt(formData.km_heen);
    const kmTerug = parseInt(formData.km_terug) || 0;
    const tarief = parseFloat(formData.tarief) || 0.25;

    try {
      if (editRit) {
        await kilometerregistratieService.updateRit(editRit.id, {
          auto_id: parseInt(formData.auto_id),
          datum: formData.datum,
          bestemming: formData.bestemming,
          omschrijving: formData.omschrijving || undefined,
          km_heen: kmHeen,
          km_terug: kmTerug,
          tarief,
        });
        setSuccess('Rit bijgewerkt');
      } else {
        await kilometerregistratieService.createRit({
          auto_id: parseInt(formData.auto_id),
          datum: formData.datum,
          bestemming: formData.bestemming,
          omschrijving: formData.omschrijving || undefined,
          km_heen: kmHeen,
          km_terug: kmTerug,
          tarief,
        });
        setSuccess('Rit geregistreerd');
      }
      setShowModal(false);
      loadRitten();
      if (activeTab === 'overzicht') loadOverzicht();
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    }
  };

  // Delete rit
  const handleDelete = async () => {
    if (!editRit || !window.confirm('Weet je zeker dat je deze rit wilt verwijderen?')) return;

    try {
      await kilometerregistratieService.deleteRit(editRit.id);
      setSuccess('Rit verwijderd');
      setShowModal(false);
      loadRitten();
      if (activeTab === 'overzicht') loadOverzicht();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen');
    }
  };

  // Auto toevoegen
  const handleAutoToevoegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nieuweAutoNaam.trim()) return;

    try {
      await kilometerregistratieService.createAuto({
        naam: nieuweAutoNaam.trim(),
        kenteken: nieuweAutoKenteken.trim() || undefined,
      });
      setSuccess('Auto toegevoegd');
      setNieuweAutoNaam('');
      setNieuweAutoKenteken('');
      loadAutos();
    } catch (err: any) {
      setError(err.message || 'Fout bij toevoegen auto');
    }
  };

  // Auto bewerken
  const startEditAuto = (auto: PriveAuto) => {
    setEditAutoId(auto.id);
    setEditAutoNaam(auto.naam);
    setEditAutoKenteken(auto.kenteken || '');
  };

  const handleAutoOpslaan = async () => {
    if (!editAutoId || !editAutoNaam.trim()) return;
    try {
      await kilometerregistratieService.updateAuto(editAutoId, {
        naam: editAutoNaam.trim(),
        kenteken: editAutoKenteken.trim() || undefined,
      });
      setEditAutoId(null);
      setSuccess('Auto bijgewerkt');
      loadAutos();
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan auto');
    }
  };

  const handleAutoVerwijderen = async (id: number) => {
    if (!window.confirm('Auto verwijderen?')) return;
    try {
      await kilometerregistratieService.deleteAuto(id);
      setSuccess('Auto verwijderd');
      loadAutos();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen auto');
    }
  };

  const kmHeen = parseInt(formData.km_heen) || 0;
  const kmTerug = parseInt(formData.km_terug) || 0;
  const kmTotaal = kmHeen + kmTerug;
  const tarief = parseFloat(formData.tarief) || 0.23;
  const vergoeding = kmTotaal * tarief;

  // Jaar opties
  const jaarOpties = [];
  const huidigJaar = new Date().getFullYear();
  for (let j = huidigJaar; j >= huidigJaar - 5; j--) {
    jaarOpties.push(j);
  }

  return (
    <div className="km-container">
      <div className="page-header">
        <h1>Kilometerregistratie</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowAutoModal(true)}>
            Auto's
          </button>
          <button className="btn btn-primary" onClick={openNewModal}>
            + Rit Registreren
          </button>
        </div>
      </div>

      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />
      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      {/* Tabs */}
      <div className="km-tabs">
        <button
          className={`km-tab ${activeTab === 'ritten' ? 'active' : ''}`}
          onClick={() => setActiveTab('ritten')}
        >
          Ritten
        </button>
        <button
          className={`km-tab ${activeTab === 'overzicht' ? 'active' : ''}`}
          onClick={() => setActiveTab('overzicht')}
        >
          Jaaroverzicht
        </button>
      </div>

      {/* Filters */}
      <div className="km-filters">
        {autos.length > 1 && (
          <div className="km-filter-group">
            <label>Auto</label>
            <select
              value={filterAutoId || ''}
              onChange={(e) => setFilterAutoId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Alle auto's</option>
              {autos.map(a => (
                <option key={a.id} value={a.id}>{a.naam}</option>
              ))}
            </select>
          </div>
        )}
        <div className="km-filter-group">
          <label>Jaar</label>
          <select
            value={filterJaar}
            onChange={(e) => setFilterJaar(parseInt(e.target.value))}
          >
            {jaarOpties.map(j => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab content: Ritten */}
      {activeTab === 'ritten' && (
        <>
          {loading ? (
            <div className="km-loading">Laden...</div>
          ) : ritten.length === 0 ? (
            <div className="km-empty">
              {autos.length === 0
                ? 'Voeg eerst een auto toe via de "+ Auto" knop.'
                : 'Geen ritten gevonden. Registreer je eerste rit!'}
            </div>
          ) : (
            <table className="km-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  {autos.length > 1 && <th>Auto</th>}
                  <th>Bestemming</th>
                  <th>Omschrijving</th>
                  <th className="text-right">Heen</th>
                  <th className="text-right">Terug</th>
                  <th className="text-right">Totaal</th>
                  <th className="text-right">Vergoeding</th>
                </tr>
              </thead>
              <tbody>
                {ritten.map(rit => (
                  <tr key={rit.id} onClick={() => openEditModal(rit)}>
                    <td>{new Date(rit.datum + 'T00:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    {autos.length > 1 && <td>{rit.auto_naam}</td>}
                    <td>{rit.bestemming}</td>
                    <td>{rit.omschrijving || '-'}</td>
                    <td className="text-right">{rit.km_heen} km</td>
                    <td className="text-right">{rit.km_terug} km</td>
                    <td className="text-right"><strong>{rit.km_totaal} km</strong></td>
                    <td className="text-right"><strong>{formatCurrency(rit.vergoeding)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {total > 0 && (
            <div style={{ marginTop: '1rem', color: '#6c757d', fontSize: '0.875rem' }}>
              {total} rit{total !== 1 ? 'ten' : ''} gevonden
            </div>
          )}
        </>
      )}

      {/* Tab content: Jaaroverzicht */}
      {activeTab === 'overzicht' && (
        <>
          {loading ? (
            <div className="km-loading">Laden...</div>
          ) : overzicht ? (
            <>
              <div className="km-totalen">
                <div className="km-totaal-card">
                  <div className="label">Totaal kilometers</div>
                  <div className="value">{overzicht.totaal_km.toLocaleString('nl-NL')} km</div>
                </div>
                <div className="km-totaal-card percentage">
                  <div className="label">Totaal vergoeding</div>
                  <div className="value">{formatCurrency(overzicht.totaal_vergoeding)}</div>
                </div>
              </div>

              {overzicht.autos.length === 0 ? (
                <div className="km-empty">Geen ritten geregistreerd in {overzicht.jaar}.</div>
              ) : (
                <div className="km-overzicht">
                  {overzicht.autos.map(auto => (
                    <div key={auto.auto_id} className="km-overzicht-card">
                      <h3>{auto.auto_naam}</h3>
                      <div className="km-stat-row">
                        <span className="label">Kilometers</span>
                        <span className="value">{auto.km_totaal.toLocaleString('nl-NL')} km</span>
                      </div>
                      <div className="km-stat-row">
                        <span className="label">Vergoeding</span>
                        <span className="value zakelijk">{formatCurrency(auto.vergoeding_totaal)}</span>
                      </div>
                      <div className="km-stat-row">
                        <span className="label">Aantal ritten</span>
                        <span className="value">{auto.aantal_ritten}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="km-empty">Kon overzicht niet laden.</div>
          )}
        </>
      )}

      {/* Rit Modal */}
      {showModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editRit ? 'Rit bewerken' : 'Rit registreren'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {autos.length > 1 && (
                  <div className="form-group">
                    <label>Auto *</label>
                    <select
                      value={formData.auto_id}
                      onChange={(e) => setFormData({ ...formData, auto_id: e.target.value })}
                      required
                    >
                      <option value="">Selecteer auto</option>
                      {autos.map(a => (
                        <option key={a.id} value={a.id}>{a.naam}{a.kenteken ? ` (${a.kenteken})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Datum *</label>
                  <input
                    type="date"
                    value={formData.datum}
                    onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Bestemming *</label>
                  <select
                    value={relaties.some(r => r.naam === formData.bestemming) ? formData.bestemming : '__custom'}
                    onChange={async (e) => {
                      if (e.target.value === '__custom') {
                        setFormData({ ...formData, bestemming: '' });
                      } else {
                        const relatie = relaties.find(r => r.naam === e.target.value);
                        setFormData({ ...formData, bestemming: e.target.value });
                        if (relatie) {
                          try {
                            const { km } = await kilometerregistratieService.getAfstand(relatie.id);
                            setFormData(prev => ({ ...prev, bestemming: e.target.value, km_heen: km.toString(), km_terug: km.toString() }));
                          } catch {
                            // Geen adres of route beschikbaar — gebruiker vult zelf in
                          }
                        }
                      }
                    }}
                  >
                    <option value="__custom">-- Vrij invoeren --</option>
                    {relaties.map(r => (
                      <option key={r.id} value={r.naam}>
                        {r.naam}{r.plaats ? ` (${r.plaats})` : ''}
                      </option>
                    ))}
                  </select>
                  {(!relaties.some(r => r.naam === formData.bestemming)) && (
                    <input
                      type="text"
                      value={formData.bestemming}
                      onChange={(e) => setFormData({ ...formData, bestemming: e.target.value })}
                      placeholder="Bijv. Kantoor - Klant X"
                      required
                      style={{ marginTop: '0.5rem' }}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Omschrijving</label>
                  <textarea
                    value={formData.omschrijving}
                    onChange={(e) => setFormData({ ...formData, omschrijving: e.target.value })}
                    placeholder="Doel van de rit"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>KM heen *</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.km_heen}
                      onChange={(e) => setFormData({ ...formData, km_heen: e.target.value })}
                      placeholder="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>KM terug</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.km_terug}
                      onChange={(e) => setFormData({ ...formData, km_terug: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Tarief per km</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.tarief}
                    onChange={(e) => setFormData({ ...formData, tarief: e.target.value })}
                  />
                </div>

                {kmTotaal > 0 && (
                  <div className="km-calculated">
                    <div>
                      <span className="label">{kmTotaal} km x {"\u20AC"}{tarief.toFixed(2)}</span>
                    </div>
                    <span className="value">{formatCurrency(vergoeding)}</span>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                {editRit && (
                  <button type="button" className="btn btn-danger" onClick={handleDelete}>
                    Verwijderen
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editRit ? 'Opslaan' : 'Registreren'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auto beheer Modal */}
      {showAutoModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowAutoModal(false); setEditAutoId(null); } }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>Auto's beheren</h2>
              <button className="modal-close" onClick={() => { setShowAutoModal(false); setEditAutoId(null); }}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Bestaande auto's */}
              {autos.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  {autos.map(auto => (
                    <div key={auto.id} className="km-auto-row">
                      {editAutoId === auto.id ? (
                        <>
                          <div style={{ flex: 1, display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="text"
                              value={editAutoNaam}
                              onChange={(e) => setEditAutoNaam(e.target.value)}
                              placeholder="Naam"
                              style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.875rem' }}
                            />
                            <input
                              type="text"
                              value={editAutoKenteken}
                              onChange={(e) => setEditAutoKenteken(e.target.value)}
                              placeholder="Kenteken"
                              style={{ width: '110px', padding: '0.4rem 0.6rem', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.875rem' }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button className="btn btn-primary btn-sm" onClick={handleAutoOpslaan}>Opslaan</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditAutoId(null)}>Annuleren</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ flex: 1 }}>
                            <strong>{auto.naam}</strong>
                            {auto.kenteken && <span style={{ color: '#6c757d', marginLeft: '0.5rem' }}>{auto.kenteken}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => startEditAuto(auto)}>Bewerken</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleAutoVerwijderen(auto.id)}>Verwijderen</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Nieuwe auto toevoegen */}
              <form onSubmit={handleAutoToevoegen}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#495057' }}>Nieuwe auto toevoegen</div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <input
                    type="text"
                    value={nieuweAutoNaam}
                    onChange={(e) => setNieuweAutoNaam(e.target.value)}
                    placeholder="Naam"
                    required
                    style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  <input
                    type="text"
                    value={nieuweAutoKenteken}
                    onChange={(e) => setNieuweAutoKenteken(e.target.value)}
                    placeholder="Kenteken"
                    style={{ width: '110px', padding: '0.4rem 0.6rem', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.875rem' }}
                  />
                  <button type="submit" className="btn btn-primary btn-sm">Toevoegen</button>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowAutoModal(false); setEditAutoId(null); }}>Sluiten</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KilometerRegistratie;
